import type { ProcessStatus } from '@/types/workOrder.types';

// Insurance Types
export interface Insurance {
  insuranceId?: string | null;
  adjuster?: string | null;
  adjusterName?: string | null;
  insuredName?: string | null;
  phone?: string | null;
  fax?: string | null;
  dateOfLoss?: string | null;
  billTo?: string | null;
  policy?: string | null;
  claim: string;
  createdById?: string | null;
  createdAt?: Date | null;
  updatedAt?: Date | null;
  deletedAt?: Date | null;
}

export enum RushType {
  NEXT_DAY = 'NEXT_DAY',
  SAME_DAY = 'SAME_DAY',
}

export type Jurisdiction = 'FEDERAL' | 'STATE';

// Case Base Types
export interface Case {
  number: string;
  name: string;
  jurisdiction: Jurisdiction;
  fileNo: string;
  plaintiff: string;
  defendant: string;
  rush: RushType;
  serveBy: Date;
  hearingOn?: Date | null;
  courtId: string;
  typeId: string;
  attorneyId: string;
  attorney?: Contact;
  insurance?: Insurance | null;
  address1?: string | null;
  address2?: string | null;
  city?: string | null;
  state?: string | null;
  zipCode?: string | null;
}

// Case Create Request
export interface CaseCreateRequest extends Case {
  status?: ProcessStatus | null;
}

// Case Update Request
export interface CaseUpdateRequest extends Omit<Case, 'serveBy' | 'hearingOn'> {
  caseId: string;
  serveBy: Date;
  hearingOn?: Date | null;
  status: ProcessStatus;
}

// Case Update Patch Request
export interface CaseUpdatePatchRequest extends Omit<Case, 'serveBy' | 'hearingOn' | 'insurance'> {
  caseId: string;
  serveBy: Date;
  hearingOn?: Date | null;
  status: ProcessStatus;
  insurance?: Partial<Insurance> | null;
}

// Case Response
export interface CaseResponse {
  caseId: string;
  number: string;
  name: string;
  jurisdiction: Jurisdiction;
  fileNo: string;
  plaintiff: string;
  defendant: string;
  rush: RushType;
  serveBy: string;
  hearingOn?: string | null;
  serviceId?: string | null;
  processServiceId?: string | null;
  attorneyId: string;
  attorney: Contact;
  courtId: string;
  court: Court;
  typeId: string;
  type: CaseType;
  insurance?: Insurance | null;
  address1?: string | null;
  address2?: string | null;
  city?: string | null;
  state?: string | null;
  zipCode?: string | null;
  createdById?: string | null;
  status: ProcessStatus;
  createdAt: Date;
  updatedAt: Date;
  deletedAt?: Date | null;
}

// Case Delete Response
export interface CaseDelete {
  message?: string;
}

export interface Contact {
  contactId: string;
  firstName: string;
  lastName: string;
  email: string;
  phone?: string;
}

export interface Court {
  courtId: string;
  name: string;
  address1: string;
  address2?: string | null;
  city: string;
  state: string;
  zipCode: string;
  phone?: string | null;
  fax?: string | null;
}

export interface CaseGetAllQuery {
  pageNo?: number | string;
  pageSize?: number | string;
  sortField?: 'number' | 'name' | 'jurisdiction' | 'plaintiff' | 'defendant' | 'status' | 'createdAt' | 'updatedAt';
  sortDirection?: 'asc' | 'desc';
  jurisdiction?: Jurisdiction;
  status?: ProcessStatus;
  search?: string;
}

export type CaseGetAllResponse = CaseResponse[];

// Export type aliases for compatibility
export type CaseSchema = Case;
// Case Document Type Base Types
export interface CaseDocumentType {
  caseDocTypeId?: string | null;
  docTypeId: string;
  caseTypeId: string;
  docType?: DocumentType;
}

// Case Document Type Create Request
export interface CaseDocumentTypeCreateRequest {
  caseTypeId: string;
  documentName: string;
}

// Case Document Type Update Request
export interface CaseDocumentTypeUpdateRequest {
  caseDocTypeId?: string | null;
  docTypeId?: string;
  caseTypeId?: string;
}

// Case Document Type Message Response
export interface CaseDocumentTypeMessageResponse {
  message?: string | null;
}

// Case Document Type Response
export interface CaseDocumentTypeResponse {
  caseDocTypeId?: string | null;
  docTypeId: string;
  caseTypeId: string;
  caseType?: CaseType | null;
  docType: DocumentType;
  createdAt?: Date | null;
  updatedAt?: Date | null;
  deletedAt?: Date | null;
}

// Case Document Type Get All Response
export interface CaseDocumentTypeGetAllResponse {
  caseDocumentTypes: CaseDocumentTypeResponse[];
}

// Case Document Type Query Request
export interface CaseDocumentTypeQueryRequest {
  sortField?: 'createdAt' | 'updatedAt' | 'docType' | 'caseType';
  sortDirection?: 'asc' | 'desc';
  search?: string;
  caseTypeId?: string;
  docTypeId?: string;
}

export interface DocumentType {
  docTypeId: string;
  name: string;
  description?: string | null;
  isActive?: boolean;
  createdAt?: Date;
  updatedAt?: Date;
  deletedAt?: Date | null;
}
export interface CaseType {
  caseTypeId: string;
  name: string;
  description?: string | null;
  isActive?: boolean;
  createdAt?: Date;
  updatedAt?: Date;
  deletedAt?: Date | null;
}

export type CaseDocumentTypeListResponse = CaseDocumentTypeResponse[];

export type CaseDocumentTypeSchema = CaseDocumentType;
